import API_BASE_URL from '../config';
import authFetch from '../utils/authFetch';
const bodyMarkingService = {
  /**
   * Get all body markings for a specific health record
   * @param {number} hrInfoId - The health record ID
   * @returns {Promise} - Returns grouped body markings {front: [], back: []}
   */
  getByHrInfoId: async (hrInfoId) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-body-markings/hr-info/${hrInfoId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch body markings');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching body markings:', error);
      throw error;
    }
  },

  /**
   * Save body markings in bulk (replaces all existing markings)
   * @param {number} hrInfoId - The health record ID
   * @param {Object} bodyMarks - Object with front and back arrays: {front: [], back: []}
   * @returns {Promise}
   */
  saveBulk: async (hrInfoId, bodyMarks) => {
    try {
      // Flatten the front and back arrays into a single array with body_side property
      const markings = [];
      
      if (bodyMarks.front && Array.isArray(bodyMarks.front)) {
        bodyMarks.front.forEach(mark => {
          markings.push({
            body_side: 'front',
            mark_type: mark.type,
            position_x: mark.x,
            position_y: mark.y,
            description: mark.description || ''
          });
        });
      }
      
      if (bodyMarks.back && Array.isArray(bodyMarks.back)) {
        bodyMarks.back.forEach(mark => {
          markings.push({
            body_side: 'back',
            mark_type: mark.type,
            position_x: mark.x,
            position_y: mark.y,
            description: mark.description || ''
          });
        });
      }
      const response = await authFetch(`${API_BASE_URL}/api/hr-body-markings/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          hr_info_id: hrInfoId,
          markings: markings
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Failed to save body markings: ' + errorText);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error saving body markings:', error);
      throw error;
    }
  },

  /**
   * Create a single body marking
   * @param {Object} marking - The marking data
   * @returns {Promise}
   */
  create: async (marking) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-body-markings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(marking)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create body marking');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating body marking:', error);
      throw error;
    }
  },

  /**
   * Update a body marking
   * @param {number} id - The marking ID
   * @param {Object} marking - The updated marking data
   * @returns {Promise}
   */
  update: async (id, marking) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-body-markings/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(marking)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update body marking');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating body marking:', error);
      throw error;
    }
  },

  /**
   * Delete a single body marking
   * @param {number} id - The marking ID
   * @returns {Promise}
   */
  delete: async (id) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-body-markings/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete body marking');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting body marking:', error);
      throw error;
    }
  },

  /**
   * Delete all body markings for a health record
   * @param {number} hrInfoId - The health record ID
   * @returns {Promise}
   */
  deleteByHrInfoId: async (hrInfoId) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-body-markings/hr-info/${hrInfoId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete body markings');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting body markings:', error);
      throw error;
    }
  }
};

export default bodyMarkingService;
